import React, { useState, useEffect } from "react";
import { Search, BookOpen, Users, Clock, Plus, Loader2, AlertCircle, Check, Trash2, Edit2 } from "lucide-react";
import { ApiService } from "../../../services/api";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import Modal from "../../../components/ui/Modal";

interface Department {
  id: string;
  name: string;
  parentDepartmentId?: string;
  parentName?: string;
}

interface Lesson {
  id: string;
  name: string;
  duration: number;
  code?: string;
  assignedHours?: number;
}

interface ClassAssignment {
  id: string;
  classId: string;
  lessonId: string;
  hours: number;
  lesson?: {
    id: string;
    name: string;
    code: string;
  };
}

interface ClassTotalInfo {
  classId: string;
  className: string;
  totalLessonHours: number;
  departmentIds?: string[];
}

const ClassLessonAssignments = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState<string | null>(null);

  const [classes, setClasses] = useState<Department[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [assignments, setAssignments] = useState<ClassAssignment[]>([]);
  const [classTotalInfos, setClassTotalInfos] = useState<ClassTotalInfo[]>([]);

  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);

  const [allDepartmentsMap, setAllDepartmentsMap] = useState<Record<string, string>>({});
  const [studentDeptId, setStudentDeptId] = useState<string | null>(null);

  const [lessonHoursInput, setLessonHoursInput] = useState<Record<string, number>>({});

  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState<string | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Edit hours modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<ClassAssignment | null>(null);
  const [editHoursValue, setEditHoursValue] = useState<number>(1);
  const [savingHours, setSavingHours] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (studentDeptId) fetchStructuralDepartments(studentDeptId);
  }, [studentDeptId]);

  useEffect(() => {
    if (selectedClass) {
      fetchClassAssignments(selectedClass);
    } else {
      setAssignments([]);
    }
  }, [selectedClass]);

  const fetchInitialData = async () => {
    try {
      setLoadingInitial(true);
      const settingsRes = await ApiService.getSettings();
      const settings = settingsRes?.data ?? settingsRes;

      if (settings?.studentDepartmentId) {
        setStudentDeptId(settings.studentDepartmentId);
      }

      await Promise.all([
        fetchLessons(),
        fetchClassTotals()
      ]);
    } catch (e) {
      console.error("Veri yükleme hatası:", e);
    } finally {
      setLoadingInitial(false);
    }
  };

  const parseListResponse = (res: any) => {
    const data = (res?.data ?? res) as any;
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    return [];
  };

  const fetchStructuralDepartments = async (rootId: string) => {
    try {
      const categoriesRes = await ApiService.getDepartments(1, 100, `&parentId=${rootId}`);
      const categories = parseListResponse(categoriesRes);

      if (!Array.isArray(categories) || categories.length === 0) return;

      const map: Record<string, string> = { ...allDepartmentsMap };
      const leafs: Department[] = [];

      categories.forEach((cat: any) => { map[cat.id] = cat.name; });

      const promises = categories.map(cat =>
        ApiService.getDepartments(1, 100, `&parentId=${cat.id}`)
          .then(res => {
            const list = parseListResponse(res);
            if (Array.isArray(list)) {
              return list.map((l: any) => ({
                ...l,
                parentDepartmentId: cat.id,
                parentName: cat.name
              }));
            }
            return [];
          })
          .catch(() => [])
      );

      const results = await Promise.all(promises);
      results.forEach(list => {
        list.forEach((cls: Department) => {
          leafs.push(cls);
          map[cls.id] = cls.name;
        });
      });

      setClasses(leafs);
      setAllDepartmentsMap(map);
    } catch (e) {
      console.error("Departman yapısı çekilemedi:", e);
    }
  };

  const fetchLessons = async () => {
    try {
      const res = await ApiService.getPlanningLessons({ limit: 100, isActive: true });
      const lessonsList: Lesson[] = parseListResponse(res).map((l: any) => ({
        id: l.id,
        name: l.name,
        code: l.code,
        duration: l.duration || 1
      }));
      setLessons(lessonsList);

      const inputs: Record<string, number> = {};
      lessonsList.forEach(l => {
        inputs[l.id] = 2;
      });
      setLessonHoursInput(inputs);
    } catch (e) {
      console.error("Dersler yüklenemedi:", e);
    }
  };

  const fetchClassTotals = async () => {
    try {
      const res = await ApiService.getAllClassesTotalHours();
      setClassTotalInfos(Array.isArray(res) ? res : (res as any).data || []);
    } catch (e) {
      console.error("Sınıf toplamları yüklenemedi:", e);
    }
  };

  const fetchClassAssignments = async (classId: string) => {
    setLoadingAssignments(true);
    try {
      const res = await ApiService.getClassLessonAssignments(classId);
      setAssignments(parseListResponse(res));
    } catch (e) {
      console.error("Atamalar çekilemedi:", e);
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleHourChange = (lessonId: string, val: string) => {
    const num = parseInt(val);
    if (!isNaN(num) && num >= 0) {
      setLessonHoursInput(prev => ({ ...prev, [lessonId]: num }));
    }
  };

  const addAssignment = async (lessonId: string) => {
    if (!selectedClass) return;
    const hours = lessonHoursInput[lessonId] || 1;
    if (hours <= 0) return;

    setSubmitting(lessonId);
    try {
      await ApiService.upsertClassLessonAssignment(selectedClass, lessonId, hours);
      await Promise.all([
        fetchClassAssignments(selectedClass),
        fetchClassTotals()
      ]);
    } catch (e: any) {
      console.error("Atama yapılamadı:", e);
      setErrorMessage("Atama sırasında hata oluştu: " + (e.message || "Bilinmeyen hata"));
      setErrorModalOpen(true);
    } finally {
      setSubmitting(null);
    }
  };

  const handleRemoveClick = (lessonId: string) => {
    setLessonToDelete(lessonId);
    setDeleteModalOpen(true);
  };

  const confirmRemoveAssignment = async () => {
    if (!selectedClass || !lessonToDelete) return;
    try {
      await ApiService.deleteClassLessonAssignment(selectedClass, lessonToDelete);
      await Promise.all([
        fetchClassAssignments(selectedClass),
        fetchClassTotals()
      ]);
    } catch (e) {
      console.error("Silme hatası:", e);
    } finally {
      setDeleteModalOpen(false);
      setLessonToDelete(null);
    }
  };

  const handleBulkDeleteClick = () => {
    if (!selectedClass) return;
    setBulkDeleteModalOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (!selectedClass) return;
    setBulkDeleting(true);
    try {
      await ApiService.bulkDeleteClassAssignments(selectedClass);
      await Promise.all([
        fetchClassAssignments(selectedClass),
        fetchClassTotals()
      ]);
    } catch (e: any) {
      console.error("Toplu silme hatası:", e);
      setErrorMessage(e.response?.data?.message || "Toplu silme sırasında hata oluştu.");
      setErrorModalOpen(true);
    } finally {
      setBulkDeleting(false);
      setBulkDeleteModalOpen(false);
    }
  };

  const openEditModal = (assignment: ClassAssignment) => {
    setEditingAssignment(assignment);
    setEditHoursValue(assignment.hours);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingAssignment(null);
    setEditHoursValue(1);
  };

  const saveEditedHours = async () => {
    if (!selectedClass || !editingAssignment || editHoursValue < 1) return;

    setSavingHours(true);
    try {
      await ApiService.updateClassLessonAssignmentHours(selectedClass, editingAssignment.lessonId, editHoursValue);
      await Promise.all([
        fetchClassAssignments(selectedClass),
        fetchClassTotals()
      ]);
      closeEditModal();
    } catch (e: any) {
      console.error("Saat güncelleme hatası:", e);
      setErrorMessage(e.response?.data?.message || "Saat güncellenirken hata oluştu.");
      setErrorModalOpen(true);
    } finally {
      setSavingHours(false);
    }
  };

  const groupedClasses = React.useMemo(() => {
    const groups: Record<string, Department[]> = {};
    classes.forEach(cls => {
      const key = cls.parentName || "Diğer";
      if (!groups[key]) groups[key] = [];
      groups[key].push(cls);
    });
    return groups;
  }, [classes]);

  const filteredGroups = Object.keys(groupedClasses).reduce((acc, groupName) => {
    const filtered = groupedClasses[groupName].filter(cls =>
      cls.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) acc[groupName] = filtered;
    return acc;
  }, {} as Record<string, Department[]>);

  const getInfoForClass = (classId: string) => {
    return classTotalInfos.find(i => i.departmentIds?.includes(classId));
  };

  const isAssigned = (lessonId: string) => assignments.some(a => a.lessonId === lessonId);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <p className="text-sm text-gray-600">Sınıflara ders ve saat ataması yapın</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Sınıflar</h3>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Sınıf ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-2 flex-1 overflow-y-auto max-h-[600px]">
                {loadingInitial ? (
                  <div className="flex justify-center p-4"><Loader2 className="animate-spin text-blue-500" /></div>
                ) : Object.entries(filteredGroups).map(([groupName, groupClasses]) => (
                  <div key={groupName} className="mb-4">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1 sticky top-0 bg-gray-50 py-1">{groupName}</h4>
                    <div className="space-y-1">
                      {groupClasses.map(cls => {
                        const info = getInfoForClass(cls.id);
                        return (
                          <div
                            key={cls.id}
                            onClick={() => setSelectedClass(cls.id)}
                            className={`p-3 rounded-lg cursor-pointer transition-all border flex justify-between items-center ${selectedClass === cls.id
                              ? "bg-blue-100 border-blue-300 text-blue-900 shadow-sm"
                              : "bg-white border-gray-200 hover:bg-gray-100 hover:border-gray-300"
                              }`}
                          >
                            <div className="flex items-center space-x-2">
                              <Users size={16} className={selectedClass === cls.id ? "text-blue-600" : "text-gray-400"} />
                              <span className="font-medium">{cls.name}</span>
                            </div>
                            {info && (info.totalLessonHours > 0) && (
                              <div className="flex flex-col items-end text-xs">
                                <span className={selectedClass === cls.id ? "text-blue-700 font-semibold" : "text-gray-600"}>
                                  {info.totalLessonHours} Saat
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {!loadingInitial && Object.keys(filteredGroups).length === 0 && (
                  <div className="text-center text-gray-500 py-4">Sınıf bulunamadı.</div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className={`bg-gray-50 rounded-lg p-4 h-full flex flex-col transition-opacity duration-200 ${!selectedClass ? 'opacity-70 pointer-events-none grayscale-[0.5]' : ''}`}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-900">Ders Havuzu</h3>
                {!selectedClass && <span className="text-xs text-red-500 font-medium">Sınıf Seçiniz</span>}
              </div>

              <div className="space-y-2 flex-1 overflow-y-auto max-h-[600px]">
                {lessons.map(lesson => {
                  const assigned = isAssigned(lesson.id);
                  return (
                    <div key={lesson.id} className={`bg-white rounded-lg p-3 border ${assigned ? 'border-green-200 bg-green-50' : 'border-gray-200'} transition-all hover:shadow-sm`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 overflow-hidden">
                          <BookOpen size={16} className={assigned ? "text-green-600" : "text-blue-500"} />
                          <div className="flex flex-col truncate">
                            <span className={`font-medium truncate ${assigned ? 'text-green-900' : 'text-gray-900'}`}>{lesson.name}</span>
                            {lesson.code && <span className="text-xs text-gray-400">{lesson.code}</span>}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 shrink-0">
                          <div className="flex items-center bg-gray-100 rounded px-1 group focus-within:ring-1 focus-within:ring-blue-400">
                            <Clock size={12} className="text-gray-400 mr-1" />
                            <input
                              type="number"
                              min="1"
                              max="40"
                              className="w-8 bg-transparent text-sm text-center focus:outline-none"
                              value={lessonHoursInput[lesson.id] || ''}
                              onChange={(e) => handleHourChange(lesson.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>

                          <button
                            onClick={() => addAssignment(lesson.id)}
                            disabled={submitting === lesson.id}
                            className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${assigned
                              ? "bg-green-500 text-white hover:bg-green-600 shadow-sm" // Update mode implied if allowed, but API does upsert
                              : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
                              }`}
                            title={assigned ? "Saati Güncelle" : "Ekle"}
                          >
                            {submitting === lesson.id ? <Loader2 className="animate-spin" size={14} /> : (assigned ? <Check size={14} /> : <Plus size={14} />)}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">
                  {selectedClass ? `${classes.find(c => c.id === selectedClass)?.name || 'Sınıf'} - Atanan Dersler` : 'Atanan Dersler'}
                </h3>
                {selectedClass && assignments.length > 0 && (
                  <button
                    onClick={handleBulkDeleteClick}
                    disabled={bulkDeleting}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Tüm dersleri kaldır"
                  >
                    {bulkDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  </button>
                )}
              </div>

              <div className="space-y-2 flex-1 overflow-y-auto max-h-[600px]">
                {loadingAssignments ? (
                  <div className="flex justify-center p-8"><Loader2 className="animate-spin text-gray-400" /></div>
                ) : (
                  <>
                    {assignments.map(assign => (
                      <div key={assign.id} className="bg-white rounded-lg p-3 border border-gray-200 group hover:border-blue-200 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-sm font-bold cursor-pointer hover:bg-green-200 transition-colors"
                              onClick={() => openEditModal(assign)}
                              title="Saati düzenlemek için tıklayın"
                            >
                              {assign.hours}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{assign.lesson?.name || 'Ders'}</div>
                              <div className="text-xs text-gray-500">{assign.lesson?.code}</div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEditModal(assign)}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Saati düzenle"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleRemoveClick(assign.lessonId)}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Kaldır"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {!selectedClass && (
                      <div className="text-center py-10 text-gray-400 flex flex-col items-center">
                        <Users size={40} className="mb-2 opacity-50" />
                        <span>Görüntülemek için sınıf seçin</span>
                      </div>
                    )}

                    {selectedClass && assignments.length === 0 && (
                      <div className="text-center py-10 text-gray-400 flex flex-col items-center">
                        <AlertCircle size={40} className="mb-2 opacity-50" />
                        <span>Henüz ders atanmamış</span>
                        <span className="text-xs mt-1">Ortadaki panelden ders ekleyin</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {selectedClass && assignments.length > 0 && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Toplam Ders:</span>
                    <span className="font-bold text-gray-900">{assignments.length}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-1">
                    <span className="text-gray-600">Toplam Saat:</span>
                    <span className="font-bold text-blue-600">{assignments.reduce((sum, a) => sum + a.hours, 0)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Delete Single Assignment Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setLessonToDelete(null);
        }}
        onConfirm={confirmRemoveAssignment}
        title="Ders Kaldır"
        message="Bu dersi sınıftan kaldırmak istediğinize emin misiniz?"
        confirmText="Kaldır"
        cancelText="İptal"
        confirmButtonVariant="danger"
      />

      {/* Bulk Delete Modal */}
      <ConfirmModal
        isOpen={bulkDeleteModalOpen}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={confirmBulkDelete}
        title="Tüm Dersleri Kaldır"
        message="Bu sınıftaki tüm ders atamalarını silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmText="Tümünü Sil"
        cancelText="İptal"
        confirmButtonVariant="danger"
        isLoading={bulkDeleting}
      />

      {/* Edit Hours Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={closeEditModal}
        title="Ders Saatini Düzenle"
        size="sm"
      >
        <div className="space-y-4">
          {editingAssignment && (
            <>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="text-base font-medium text-gray-900">{editingAssignment.lesson?.name}</div>
                  <div className="text-sm text-gray-500">{editingAssignment.lesson?.code}</div>
                </div>
                <div className="flex-shrink-0">
                  <label className="block text-xs font-medium text-gray-500 mb-1 text-center">Haftalık Saat</label>
                  <input
                    type="number"
                    min="1"
                    max="40"
                    value={editHoursValue}
                    onChange={(e) => setEditHoursValue(parseInt(e.target.value) || 1)}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-center text-lg font-semibold"
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={closeEditModal}
                  disabled={savingHours}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium text-sm transition-colors disabled:opacity-50"
                >
                  İptal
                </button>
                <button
                  onClick={saveEditedHours}
                  disabled={savingHours || editHoursValue < 1}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {savingHours ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    'Kaydet'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Error Modal */}
      <ConfirmModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        onConfirm={() => setErrorModalOpen(false)}
        title="Hata"
        message={errorMessage}
        confirmText="Tamam"
        cancelText=""
        confirmButtonVariant="primary"
      />
    </div>
  );
};

export default ClassLessonAssignments;